import axios from 'axios';
import i18next from 'i18next';
import { Category } from 'src/models';
import { getRootStoreSingleton } from 'src/models/root-store/root-store-singleton';

import { DEFAULT_AXIOS_CONFIG } from './axiosConfig';

// Axios 인스턴스 생성
const axiosInstance = axios.create(DEFAULT_AXIOS_CONFIG);

// 요청 인터셉터 설정
axiosInstance.interceptors.request.use(
  (config) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(true);

    const token = getRootStoreSingleton().authStore.getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // i18nextLng_source에 따라 언어 소스 결정
    // 'user': 사용자 명시 선택 → localStorage 우선
    // 그 외: 기기 자동 감지 → i18next.language 우선
    const langSource = localStorage.getItem('i18nextLng_source');
    config.headers['Accept-Language'] =
      langSource === 'user'
        ? localStorage.getItem('i18nextLng') || i18next.language || 'ko'
        : i18next.language || 'ko';
    return config;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);
    return Promise.reject(error);
  },
);

// 응답 인터셉터 설정
axiosInstance.interceptors.response.use(
  async (response) => {
    const data = response.data;
    const config = response.config;

    const authStore = getRootStoreSingleton().authStore;
    const snackbarStore = getRootStoreSingleton().snackbarStore;
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);

    if (data.resultCode === 'F') {
      if (
        (data.errorCode === '401' || data.errorCode === '4001') &&
        !config.url?.includes('/login/refresh')
      ) {
        const refreshToken = authStore.getRefreshToken();
        if (refreshToken) {
          const result = await authStore.loginRefresh(refreshToken);
          if (result?.resultCode === 'S') {
            const oauthToken = result.data;
            if (oauthToken) {
              authStore.setAccessToken(oauthToken?.access_token);
              authStore.setRefreshToken(oauthToken?.refresh_token);
            }

            config.headers['Authorization'] = `Bearer ${authStore.getAccessToken()}`;
            // 토큰 재발급 시에도 동일한 언어 소스 로직 적용
            const retryLangSource = localStorage.getItem('i18nextLng_source');
            config.headers['Accept-Language'] =
              retryLangSource === 'user'
                ? localStorage.getItem('i18nextLng') || i18next.language || 'ko'
                : i18next.language || 'ko';
            return axiosInstance(config);
          } else {
            snackbarStore.setProps({
              open: true,
              category: Category.Error,
              title: result?.errorMessage,
            });
            authStore.initAuth();
          }
        }

        authStore.initAuth();
      } else if (data.errorCode !== '409') {
        let errorMessage = '' as string | undefined;
        if (data?.errorMessage) {
          errorMessage = data?.errorMessage;
        } else if (data?.err_msg) {
          errorMessage = data?.err_msg;
        }
        snackbarStore.setProps({
          open: true,
          category: Category.Error,
          title: errorMessage ? errorMessage : i18next.t('common.commonError'),
        });
      }
    }

    return response;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.setLoading(false);

    const snackbarStore = getRootStoreSingleton().snackbarStore;
    if (error.code === 'ERR_NETWORK') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.serverError'),
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
